import { useEffect } from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';
import { useQualityDashboardStore } from '@/stores/qualityDashboard';
import { useKnowledgeStore } from '@/stores/knowledge';

export function ConfidenceHistogram() {
  const {
    confidence,
    confidenceEntityType,
    confidenceLoading,
    fetchConfidence,
    setConfidenceEntityType,
  } = useQualityDashboardStore();
  const { entityTypes, fetchEntityTypes } = useKnowledgeStore();

  useEffect(() => {
    fetchEntityTypes();
    fetchConfidence();
  }, [fetchConfidence, fetchEntityTypes]);

  const max = Math.max(...confidence.map((b) => b.count), 1);
  const total = confidence.reduce((s, b) => s + b.count, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-navy-900">Confidence Distribution</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {total.toLocaleString()} canonicals{confidenceEntityType ? ` · ${confidenceEntityType}` : ' (all types)'}
          </p>
        </div>
        <select
          value={confidenceEntityType}
          onChange={(e) => setConfidenceEntityType(e.target.value)}
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        >
          <option value="">All entity types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      {confidenceLoading ? (
        <div className="text-xs text-slate-400 text-center py-4">
          <Loader2 size={12} className="animate-spin inline mr-1" />
          Loading...
        </div>
      ) : total === 0 ? (
        <div className="text-xs text-slate-400 text-center py-4">
          No canonicals for this filter.
        </div>
      ) : (
        <div className="flex items-end gap-1 h-32">
          {confidence.map((b, i) => {
            const pct = (b.count / max) * 100;
            const label = `${(b.lo * 100).toFixed(0)}–${(b.hi * 100).toFixed(0)}%`;
            return (
              <div
                key={i}
                className="flex-1 flex flex-col items-center gap-1"
                title={`${label}: ${b.count}`}
              >
                <div className="text-[9px] font-mono text-slate-500">{b.count}</div>
                <div
                  className={clsx(
                    'w-full rounded-t',
                    b.lo >= 0.8 ? 'bg-emerald-400' : b.lo >= 0.5 ? 'bg-amber-400' : 'bg-rose-400',
                  )}
                  style={{ height: `${Math.max(pct, 2)}%` }}
                />
                <div className="text-[9px] text-slate-400">{(b.lo * 100).toFixed(0)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
